import type {
  AnnouncedInvitationsDto,
  EmailInviteDto,
  InvitedPersonDto,
  OrganizationInviteDto,
} from "../../../../../../autogen/bff-api";

export type InvitationChange =
  | { kind: "companyInvited"; organizationId: string; recipients: string[] }
  | { kind: "peopleAdded"; organizationId: string; recipients: string[] }
  | { kind: "companyRemoved"; organizationId: string }
  | { kind: "entryInvited"; entryName: string; emails: string[] }
  | { kind: "emailInvitationsWithdrawn"; count: number };

export const invitationChanges = (
  announced: AnnouncedInvitationsDto,
  organizationInvites: OrganizationInviteDto[],
  emailInvites: EmailInviteDto[],
): InvitationChange[] => {
  const announcedByOrganization = new Map(announced.organizationInvites.map((i) => [i.organizationId, i]));
  const liveOrganizations = new Set(organizationInvites.map((i) => i.organizationId));
  const changes: InvitationChange[] = [];

  organizationInvites.forEach((invite) => {
    const before = announcedByOrganization.get(invite.organizationId);
    const persons = invite.persons ?? [];
    const emails = (invite.emailInvites ?? []).map((e) => e.email);
    if (!before) {
      changes.push({
        kind: "companyInvited",
        organizationId: invite.organizationId,
        recipients: [...persons.map(personLabel), ...emails],
      });
      return;
    }
    const knownEmails = new Set(before.emails.map(normalized));
    const recipients = [
      ...persons.filter((p) => !before.personIds.includes(p.id)).map(personLabel),
      ...emails.filter((e) => !knownEmails.has(normalized(e))),
    ];
    if (recipients.length > 0) changes.push({ kind: "peopleAdded", organizationId: invite.organizationId, recipients });
  });

  announced.organizationInvites
    .filter((invite) => !liveOrganizations.has(invite.organizationId))
    .forEach((invite) => changes.push({ kind: "companyRemoved", organizationId: invite.organizationId }));

  const announcedEmailInvites = new Set(announced.emailInviteIds);
  const byEntry = new Map<string, string[]>();
  emailInvites
    .filter((invite) => !announcedEmailInvites.has(invite.id))
    .forEach((invite) => {
      const entryName = invite.organizationEntry?.name ?? "";
      byEntry.set(entryName, [...(byEntry.get(entryName) ?? []), invite.email]);
    });
  byEntry.forEach((emails, entryName) => changes.push({ kind: "entryInvited", entryName, emails }));

  const liveEmailInvites = new Set(emailInvites.map((invite) => invite.id));
  const withdrawn = announced.emailInviteIds.filter((id) => !liveEmailInvites.has(id)).length;
  if (withdrawn > 0) changes.push({ kind: "emailInvitationsWithdrawn", count: withdrawn });

  return changes;
};

const personLabel = (person: InvitedPersonDto) => {
  const name = `${person.firstName ?? ""} ${person.lastName ?? ""}`.trim();
  const address = person.email ?? person.id;
  return name ? `${name} (${address})` : address;
};

const normalized = (email: string) => email.trim().toLowerCase();
